import type { RouteLocationNormalized } from 'vue-router';
import { NOTIFICATION_TYPE } from '~/composables/useNotifications';
import { LOCALE_PREFIX_PATTERN } from '~/utils/locales';
import { buildLoginUrl, isLocaleRootPath } from '~/utils/loginRedirect';

export async function authenticationMiddleware (to: RouteLocationNormalized) {
  const { checkAuthenticationStatus } = useAuthentication();
  const { getUnreadNotifications, markAsRead } = useNotifications();
  const { $i18n } = useNuxtApp();
  const localePath = useLocalePath();

  // Extract path without locale prefix for comparison
  const toPathWithoutLocale = to.path.replace(LOCALE_PREFIX_PATTERN, '');
  // Exclude routes that do not require authentication
  if (
    to.path.includes('/invite/accept') ||
    toPathWithoutLocale === '/auth/login' ||
    toPathWithoutLocale === '/auth/signup' ||
    toPathWithoutLocale === '/account/deletion-cancelled'
  ) {
    return;
  }

  const isAuthenticated = await checkAuthenticationStatus();

  // unknown paths are handled by 02.redirects (legacy redirects + login)
  if (!isAuthenticated && to.matched.length === 0 && !isLocaleRootPath(to.path)) {
    return;
  }

  const isResetPassword = toPathWithoutLocale === '/auth/reset-password';
  const isForgotPassword = toPathWithoutLocale === '/auth/forgot-password';

  if (isResetPassword || isForgotPassword) {
    if (isAuthenticated) {
      if (isResetPassword) {
        return navigateTo('/dashboard');
      }

      if (isForgotPassword) {
        return navigateTo('/account/profile-info');
      }
    }

    return;
  }

  if (!isAuthenticated) {
    // this is required to prevent 404 error when not authenticated.
    await clearError();

    return navigateTo(buildLoginUrl(to, localePath('/auth/login')), {
      replace: true,
    });
  }

  const { user } = useUserStore();

  if (toPathWithoutLocale === '/auth/verify-email') {
    return;
  }

  if (!user?.value?.isVerified) {
    const locale = $i18n?.locale?.value || 'en';
    const verifyUrl = locale === 'en' ? '/auth/verify-email' : `/${locale}/auth/verify-email`;
    return navigateTo(verifyUrl, {
      replace: true,
    });
  }

  if (toPathWithoutLocale === '/auth/about-you') {
    return;
  }

  if (!user?.value?.registrationCompleted && !to.path.includes('/auth/about-you')) {
    return navigateTo({
      path: '/auth/about-you',
    }, {
      replace: true,
    });
  }

  if (user?.value?.numberOfUnreadNotifications && to.path === '/dashboard') {
    const notifications = await getUnreadNotifications();

    if (notifications?.length) {
      const groupInvitationAcceptedUnreadNotification = notifications.find(notification =>
        notification.data.notificationName === NOTIFICATION_TYPE.B2B_INVITATION_ACCEPTED &&
        !notification.readAt,
      );

      if (groupInvitationAcceptedUnreadNotification) {
        await markAsRead(groupInvitationAcceptedUnreadNotification.id);
        if (user?.value) {
          user.value.numberOfUnreadNotifications--;
        }
      }
    }
  }
}

export default defineNuxtRouteMiddleware(authenticationMiddleware);
